"use client";
import { Contact_Us_FormDetails, GetCountry } from "@/api/Api";
import { Grid, InputLabel } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { usePathname } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { useRecaptcha } from "@/contexts/RecaptchaContext";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import styles from "./contact-elchemy.module.scss";
import {
  showToast,
  TOAST_TYPE,
  FORM_TOAST_CONTAINER_ID,
} from "@/components/Common/Toast";
import * as Yup from "yup";
import Form from "../Form/Form";
import TermsConditionBtn from "../TermsConditionBtn";
import { contactUsOptions } from "@/constastants/Constant";
import { useSelector } from "react-redux";
import { getCDNImageUrl, sendEventToGTM } from "@/utils/Common";
import Image from "next/image";
import {
  trackFormLoaded,
  trackFormSubmitted,
  FORM_TYPES,
  getPageType,
} from "@/utils/gtmEvents";

/**
 * App-wide form-toast container id. The single `<Toaster>` for it lives in
 * `components/Layout.jsx` — see `components/Common/Toast/constants.js` for why it
 * is mounted there and not per-form.
 */
const TOAST_CONTAINER_ID = FORM_TOAST_CONTAINER_ID;

const ContactElchemy = ({
  options = contactUsOptions,
  handleClose,
  service = "",
  orientation = "horizontal",
  additionalInformationInitialValue = "",
  submitButtonLabel = "",
  submitButtonWidth = "163px",
  scrollToTop = false,
  productId = null,
  isUsVersion = false,
  gtmSubmitEvent = "",
}) => {
  const [contactUs, setContactUs] = useState({
    isChecked: false,
    isLoading: false,
    countryOptions: [],
    isSuccess: false,
  });
  const { isChecked, isLoading, countryOptions, isSuccess } = contactUs;
  const pathname = usePathname();
  const { country } = useSelector((state) => state.userLocation);
  const { ReCAPTCHA, loadRecaptcha, isLoaded } = useRecaptcha();

  // Load reCAPTCHA when component mounts or when form is focused
  useEffect(() => {
    if (!isLoaded) {
      loadRecaptcha();
    }
  }, [isLoaded, loadRecaptcha]);
  const getCountries = async () => {
    try {
      const countryData = await GetCountry();
      setContactUs((prev) => {
        return { ...prev, countryOptions: countryData };
      });
    } catch (error) {
      showToast("Error fetching data", {
        type: TOAST_TYPE.ERROR,
        containerId: TOAST_CONTAINER_ID,
      });
    }
  };
  useEffect(() => {
    getCountries();

    // Track FORM_LOADED event when component mounts
    const pageUrl = typeof window !== "undefined" ? window.location.href : "";
    const pageType = getPageType(pathname);
    trackFormLoaded(FORM_TYPES.CONTACT_US, pageUrl, pageType);
  }, [pathname]);

  const checkedPolicy = () => {
    setContactUs((prev) => {
      return { ...prev, isChecked: !prev.isChecked };
    });
  };

  const handleContactForm = async (values, resetForm) => {
    setContactUs((prev) => {
      return { ...prev, isLoading: true };
    });
    try {
      const payload = {
        ...values,
        // recaptcha: values.recaptcha,
        // call_time: values.call_time === "none" ? "" : values.call_time,
      };
      if (values.recaptcha !== "" && values.recaptcha !== null) {
        payload.recaptcha = values.recaptcha;
      } else {
        delete payload.recaptcha;
      }
      if (values.call_time !== "none" && values.call_time !== "") {
        payload.call_time =
          values.call_time === "none" ? null : values.call_time;
      } else {
        delete payload.call_time;
      }
      if (values.call_date !== "none" && values.call_date !== "") {
        payload.call_date =
          values.call_date === "none" ? null : values.call_date;
      } else {
        delete payload.call_date;
      }

      if (country === "US") {
        payload.form_type = "US_FORM";
      }
      const res = await Contact_Us_FormDetails(payload);
      if (res.status == 200) {
        // Track FORM_SUBMITTED event
        const pageUrl = typeof window !== "undefined" ? window.location.href : "";
        const pageType = getPageType(pathname);
        trackFormSubmitted(FORM_TYPES.CONTACT_US, pageUrl, pageType);

        if (gtmSubmitEvent) {
          sendEventToGTM(gtmSubmitEvent, { page_url: pageUrl });
        }

        setContactUs((prev) => {
          return {
            ...prev,
            isChecked: false,
            isLoading: false,
            isSuccess: true,
          };
        });
        // No success toast here: the `isSuccess` branch below replaces the form
        // with a success panel that stays up for 5s, so a toast would repeat the
        // same confirmation twice. Failures still toast — they have no panel.
        window?.grecaptcha?.reset();
        scrollToTop && window.scrollTo({ top: 0, behavior: "smooth" });
        resetForm("");
      } else {
        // Still set loading to false even if status is not 200, and tell the
        // user — otherwise the click just appears to do nothing.
        showToast("Something went wrong, please try again!", {
          type: TOAST_TYPE.ERROR,
          containerId: TOAST_CONTAINER_ID,
        });
        setContactUs((prev) => {
          return { ...prev, isLoading: false };
        });
      }
    } catch (error) {
      showToast("Something went wrong, please try again!", {
        type: TOAST_TYPE.ERROR,
        containerId: TOAST_CONTAINER_ID,
      });
      setContactUs((prev) => {
        return { ...prev, isLoading: false };
      });
    }
  };

  const selectedService = useMemo(
    () => options.find((item) => item.id == service),
    [service]
  );

  useEffect(() => {
    if (isSuccess) {
      const timeoutId = setTimeout(() => {
        setContactUs((prev) => {
          return { ...prev, isSuccess: false };
        });
        if (handleClose) {
          handleClose();
        }
      }, 5000);

      // Cleanup function to clear timeout if component unmounts
      return () => clearTimeout(timeoutId);
    }
  }, [isSuccess, handleClose]);

  return (
    <div className="">
      {/*
        Success is confirmed by the `isSuccess` panel below, not a toast — only
        failures toast from this form. The container they render into is the single
        app-wide `<Toaster>` in `components/Layout.jsx`.
      */}
      {isSuccess == false ? (
        <div className="flex flex-col gap-5">
          <Form
            handleSubmit={handleContactForm}
            initialValues={{
              // FirstName: "",
              // LastName: "",
              Name: "",
              EmailId: "",
              MobilewithCountryCode: "",
              Country: "",
              Services: "",
              info: additionalInformationInitialValue,
              product: productId,
              recaptcha: null,
              timezone: "",
              call_date: "",
              call_time: "",
              termsAccepted: false,
            }}
            validationSchema={schema}
          >
            {(formik) => {
              const {
                values,
                errors,
                handleChange,
                setFieldValue,
                handleBlur,
                touched,
              } = formik;

              useEffect(() => {
                if (selectedService && !values.Services) {
                  setFieldValue("Services", selectedService.label);
                }
              }, [selectedService]);
              const isVertical = useMemo(
                () => orientation === "vertical",
                [orientation]
              );
              const gridItemProps = useMemo(() => {
                return isVertical
                  ? { xs: 12 }
                  : { xs: 12, sm: 6, md: 4, lg: 6 };
              }, [isVertical]);

              return (
                <Grid
                  container
                  spacing={2}
                  direction={isVertical ? "column" : "row"}
                >
                  {/* Name */}
                  <Grid item {...gridItemProps}>
                    {!isUsVersion && (
                      <InputLabel
                        htmlFor="FirstName"
                        sx={{
                          marginBottom: "8px",
                          fontSize: "14px",
                          lineHeight: "21px",
                          fontFamily: "var(--font-open-sans), sans-serif",
                        }}
                      >
                        Name
                        <span className="text-red ml-1">*</span>
                      </InputLabel>
                    )}
                    <TextField
                      fullWidth
                      placeholder={isUsVersion ? "Name" : "Name"}
                      sx={{
                        paddingTop: "0px",
                        borderRadius: isUsVersion ? "8px" : "6px",
                        border: isUsVersion ? "1px solid #FAFBFC" : undefined,
                        input: {
                          height: "13px",
                          fontWeight: "normal",
                          borderColor: isUsVersion ? "#FAFBFC" : "#B3B3B3",
                          borderRadius: isUsVersion ? "8px" : "6px",
                          backgroundColor: isUsVersion ? "#F4F5F8" : "#FFF",
                        },
                        "& .MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-colorPrimary":
                          {
                            borderRadius: isUsVersion
                              ? "8px !important"
                              : "5px !important",
                          },
                        "& .MuiFormHelperText-root": {
                          marginLeft: 0,
                        },
                      }}
                      InputProps={{
                        style: {
                          borderRadius: isUsVersion ? "8px" : "10px",
                          fontSize: "16px",
                          fontWeight: "normal",
                          fontFamily: "var(--font-mukta), sans-serif",
                          background: isUsVersion ? "#F4F5F8" : undefined,
                          border: isUsVersion ? "1px solid #FAFBFC" : undefined,
                        },
                      }}
                      name="Name"
                      onBlur={handleBlur}
                      value={values?.Name}
                      onChange={handleChange}
                      error={errors.Name && touched.Name}
                      helperText={
                        errors.Name && touched.Name ? errors.Name : ""
                      }
                    />
                  </Grid>
                  {/* First Name */}
                  {/* <Grid item {...gridItemProps}>
                    {!isUsVersion && (
                      <InputLabel
                        htmlFor="FirstName"
                        sx={{
                          marginBottom: "8px",
                          fontSize: "14px",
                          lineHeight: "21px",
                          fontFamily: "var(--font-open-sans), sans-serif",
                        }}
                      >
                        Name
                        <span className="text-red ml-1">*</span>
                      </InputLabel>
                    )}
                    <TextField
                      fullWidth
                      placeholder={isUsVersion ? "Name" : "First Name"}
                      sx={{
                        paddingTop: "0px",
                        borderRadius: isUsVersion ? "8px" : "6px",
                        backgroundColor: isUsVersion ? "#F4F5F8" : "#FFF",
                        border: isUsVersion ? "1px solid #FAFBFC" : undefined,
                        input: {
                          height: "13px",
                          fontWeight: "normal",
                          borderColor: isUsVersion ? "#FAFBFC" : "#B3B3B3",
                          borderRadius: isUsVersion ? "8px" : "6px",
                          backgroundColor: isUsVersion ? "#F4F5F8" : "#FFF",
                        },
                        "& .MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-colorPrimary":
                          {
                            borderRadius: isUsVersion
                              ? "8px !important"
                              : "5px !important",
                          },
                      }}
                      InputProps={{
                        style: {
                          borderRadius: isUsVersion ? "8px" : "10px",
                          fontSize: "16px",
                          fontWeight: "normal",
                          fontFamily: "var(--font-mukta), sans-serif",
                          background: isUsVersion ? "#F4F5F8" : undefined,
                          border: isUsVersion ? "1px solid #FAFBFC" : undefined,
                        },
                      }}
                      name="FirstName"
                      onBlur={handleBlur}
                      value={values?.FirstName}
                      onChange={handleChange}
                      error={errors.FirstName && touched.FirstName}
                      helperText={
                        errors.FirstName && touched.FirstName
                          ? errors.FirstName
                          : ""
                      }
                    />
                  </Grid> */}

                  {/* Last Name */}
                  {/* <Grid item {...gridItemProps}>
                    {!isUsVersion && (
                      <InputLabel
                        htmlFor="LastName"
                        sx={{
                          marginBottom: "8px",
                          fontSize: "14px",
                          lineHeight: "21px",
                          fontFamily: "var(--font-open-sans), sans-serif",
                        }}
                      >
                        Last Name
                        <span className="text-red ml-1">*</span>
                      </InputLabel>
                    )}
                    <TextField
                      placeholder={isUsVersion ? "Last Name" : "Last Name"}
                      sx={{
                        width: "100%",
                        paddingTop: "0px",
                        borderRadius: isUsVersion ? "8px" : "6px",
                        backgroundColor: isUsVersion ? "#F4F5F8" : "#FFF",
                        border: isUsVersion ? "1px solid #FAFBFC" : undefined,
                        input: {
                          height: "13px",
                          fontWeight: "normal",
                          borderColor: isUsVersion ? "#FAFBFC" : "#B3B3B3",
                          borderRadius: isUsVersion ? "8px" : "6px",
                          backgroundColor: isUsVersion ? "#F4F5F8" : "#FFF",
                        },
                        "& .MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-colorPrimary":
                          {
                            borderRadius: isUsVersion
                              ? "8px !important"
                              : "5px !important",
                          },
                      }}
                      InputProps={{
                        style: {
                          borderRadius: isUsVersion ? "8px" : "10px",
                          fontSize: "16px",
                          fontWeight: "normal",
                          fontFamily: "var(--font-mukta), sans-serif",
                          background: isUsVersion ? "#F4F5F8" : undefined,
                          border: isUsVersion ? "1px solid #FAFBFC" : undefined,
                        },
                      }}
                      name="LastName"
                      onBlur={handleBlur}
                      value={values?.LastName}
                      onChange={handleChange}
                      error={errors.LastName && touched.LastName}
                      helperText={
                        errors.LastName && touched.LastName
                          ? errors.LastName
                          : ""
                      }
                    />
                  </Grid> */}

                  {/* Email Id */}
                  <Grid item {...gridItemProps}>
                    {!isUsVersion && (
                      <InputLabel
                        htmlFor="EmailId"
                        sx={{
                          marginBottom: "8px",
                          fontSize: "14px",
                          lineHeight: "21px",
                          fontFamily: "var(--font-open-sans), sans-serif",
                        }}
                      >
                        Email Id
                        <span className="text-red ml-1">*</span>
                      </InputLabel>
                    )}
                    <TextField
                      placeholder={isUsVersion ? "Email Id" : "Email Id"}
                      sx={{
                        width: "100%",
                        paddingTop: "0px",
                        borderRadius: isUsVersion ? "8px" : "6px",
                        border: isUsVersion ? "1px solid #FAFBFC" : undefined,
                        input: {
                          height: "13px",
                          fontWeight: "normal",
                          borderColor: isUsVersion ? "#FAFBFC" : "#B3B3B3",
                          borderRadius: isUsVersion ? "8px" : "6px",
                          backgroundColor: isUsVersion ? "#F4F5F8" : "#FFF",
                        },
                        "& .MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-colorPrimary":
                          {
                            borderRadius: isUsVersion
                              ? "8px !important"
                              : "5px !important",
                          },
                        "& .MuiFormHelperText-root": {
                          marginLeft: 0,
                        },
                      }}
                      InputProps={{
                        style: {
                          borderRadius: isUsVersion ? "8px" : "10px",
                          fontSize: "16px",
                          fontWeight: "normal",
                          fontFamily: "var(--font-mukta), sans-serif",
                          background: isUsVersion ? "#F4F5F8" : undefined,
                          border: isUsVersion ? "1px solid #FAFBFC" : undefined,
                        },
                      }}
                      name="EmailId"
                      onBlur={handleBlur}
                      value={values?.EmailId}
                      onChange={handleChange}
                      error={errors.EmailId && touched.EmailId}
                      helperText={
                        errors.EmailId && touched.EmailId ? errors.EmailId : ""
                      }
                    />
                  </Grid>

                  {/* Services */}
                  {pathname !== "/elchemy-tech" || "/" ? (
                    <Grid item {...gridItemProps}>
                      {!isUsVersion && (
                        <InputLabel
                          htmlFor="Services"
                          id="demo-simple-select-helper"
                          sx={{
                            marginBottom: "8px",
                            fontFamily: "var(--font-open-sans), sans-serif",
                            fontSize: "14px",
                            lineHeight: "21px",
                          }}
                        >
                          Services looking for
                        </InputLabel>
                      )}
                      <div>
                        <Select
                          labelId="demo-simple-select-helper-label"
                          id="demo-simple-select-helper"
                          value={values?.Services}
                          onChange={(event) => {
                            setFieldValue("Services", event.target.value);
                          }}
                          onBlur={handleBlur}
                          inputProps={{
                            fontSize: "18px",
                          }}
                          displayEmpty
                          renderValue={
                            values?.Services
                              ? undefined
                              : () => (
                                  <span
                                    style={{
                                      color: "#808080",
                                      fontStyle: "normal",
                                    }}
                                  >
                                    {" "}
                                    Select Service
                                  </span>
                                )
                          }
                          className={styles.selectField}
                          sx={{
                            width: "100%",
                            backgroundColor: isUsVersion ? "#F4F5F8" : "#FFF",
                            borderRadius: isUsVersion ? "8px" : "10px",
                            border: isUsVersion
                              ? "1px solid #FAFBFC"
                              : undefined,
                            ".MuiSelect-select": {
                              color: "black",
                              backgroundColor: isUsVersion
                                ? "#F4F5F8"
                                : undefined,
                              borderRadius: isUsVersion ? "8px" : undefined,
                            },
                            ".MuiOutlinedInput-notchedOutline": {
                              border: isUsVersion
                                ? "1px solid #FAFBFC"
                                : "1px solid #B3B3B3",
                              borderRadius: isUsVersion ? "8px" : undefined,
                            },
                            "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                              {
                                border: isUsVersion
                                  ? "1px solid #FAFBFC"
                                  : "1px solid #B3B3B3",
                              },
                            "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                              {
                                border: isUsVersion
                                  ? "1px solid #FAFBFC"
                                  : "1px solid #B3B3B3",
                              },
                          }}
                          name="Services"
                          error={errors.Services && touched.Services}
                          helperText={
                            errors.Services && touched.Services
                              ? errors.Services
                              : ""
                          }
                        >
                          <MenuItem value={""}>
                            <em
                              style={{
                                color: "#FFF",
                                fontStyle: "normal",
                                fontSize: "16px",
                              }}
                            >
                              Select service
                            </em>
                          </MenuItem>
                          {options.map((items) => (
                            <MenuItem value={items?.label} key={items?.label}>
                              {items?.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.Services && touched.Services ? (
                          <p className="text-[red]">{errors.Services}</p>
                        ) : (
                          ""
                        )}
                      </div>
                    </Grid>
                  ) : null}

                  {/* Phone No */}
                  <Grid item {...gridItemProps}>
                    {!isUsVersion && (
                      <InputLabel
                        htmlFor="MobilewithCountryCode"
                        sx={{
                          marginBottom: "8px",
                          fontSize: "14px",
                          lineHeight: "21px",
                          fontFamily: "var(--font-open-sans), sans-serif",
                        }}
                      >
                        Phone no
                      </InputLabel>
                    )}
                    <div
                      className={`${styles.phoneInputWrapper} ${
                        isUsVersion ? styles.phoneInputUs : ""
                      }`}
                    >
                      <PhoneInput
                        country={"us"}
                        enableSearch={true}
                        name="MobilewithCountryCode"
                        value={values?.MobilewithCountryCode}
                        onChange={(value) => {
                          setFieldValue("MobilewithCountryCode", value);
                        }}
                        onBlur={handleBlur}
                        error={
                          errors.MobilewithCountryCode &&
                          touched.MobilewithCountryCode
                        }
                      />
                    </div>
                  </Grid>

                  {/* Country */}
                  <Grid item {...gridItemProps}>
                    {!isUsVersion && (
                      <InputLabel
                        htmlFor="Country"
                        id="demo-simple-select-helper"
                        sx={{
                          marginBottom: "8px",
                          fontFamily: "var(--font-open-sans), sans-serif",
                          fontSize: "14px",
                          lineHeight: "21px",
                          marginTop: "8px",
                        }}
                      >
                        Country
                      </InputLabel>
                    )}
                    <div>
                      <Select
                        labelId="demo-simple-select-helper-label"
                        id="demo-simple-select-helper"
                        value={values?.Country}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: "200px",
                            },
                          },
                        }}
                        slotProps={{
                          popup: {
                            disablePortal: true,
                          },
                        }}
                        onChange={(event) => {
                          setFieldValue("Country", event.target.value);
                        }}
                        onBlur={handleBlur}
                        inputProps={{
                          fontSize: "18px",
                        }}
                        displayEmpty
                        renderValue={
                          values?.Country
                            ? undefined
                            : () => (
                                <span
                                  style={{
                                    color: "#808080",
                                    fontStyle: "normal",
                                  }}
                                >
                                  {" "}
                                  Select Country
                                </span>
                              )
                        }
                        className={styles.selectField}
                        sx={{
                          width: "100%",
                          backgroundColor: isUsVersion ? "#F4F5F8" : "#FFF",
                          borderRadius: isUsVersion ? "8px" : "10px",
                          border: isUsVersion ? "1px solid #FAFBFC" : undefined,
                          ".MuiSelect-select": {
                            color: "black",
                            backgroundColor: isUsVersion
                              ? "#F4F5F8"
                              : undefined,
                            borderRadius: isUsVersion ? "8px" : undefined,
                          },
                          ".MuiOutlinedInput-notchedOutline": {
                            border: isUsVersion
                              ? "1px solid #FAFBFC"
                              : "1px solid #B3B3B3",
                            borderRadius: isUsVersion ? "8px" : undefined,
                          },
                          "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                            {
                              border: isUsVersion
                                ? "1px solid #FAFBFC"
                                : "1px solid #B3B3B3",
                            },
                          "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                            {
                              border: isUsVersion
                                ? "1px solid #FAFBFC"
                                : "1px solid #B3B3B3",
                            },
                        }}
                        name="Country"
                        error={errors.Country && touched.Country}
                        helperText={
                          errors.Country && touched.Country
                            ? errors.Country
                            : ""
                        }
                      >
                        <MenuItem value={""}>
                          <em
                            style={{
                              color: "#FFF",
                              fontStyle: "normal",
                              fontSize: "16px",
                            }}
                          >
                            Select Country
                          </em>
                        </MenuItem>
                        {countryOptions.map((items) => (
                          <MenuItem value={items?.name} key={items?.id}>
                            {items?.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.Country && touched.Country ? (
                        <p className="text-[red]">{errors.Country}</p>
                      ) : (
                        ""
                      )}
                    </div>
                  </Grid>

                  {/* Additional Info */}
                  <Grid item xs={12}>
                    {!isUsVersion && (
                      <InputLabel
                        htmlFor="Informations"
                        sx={{
                          marginBottom: "8px",
                          fontSize: "14px",
                          lineHeight: "21px",
                          fontFamily: "var(--font-open-sans), sans-serif",
                        }}
                      >
                        Additional information that will help us connect better
                      </InputLabel>
                    )}
                    <TextField
                      sx={{
                        width: "100%",
                        borderRadius: isUsVersion ? "8px" : "6px",
                        borderColor: isUsVersion ? "#FAFBFC" : "#B3B3B3",
                        backgroundColor: isUsVersion ? "#F4F5F8" : "#FFF",
                        input: {
                          fontFamily: "var(--font-open-sans), sans-serif",
                          fontSize: "14px",
                          fontWeight: "normal",
                          backgroundColor: isUsVersion ? "#F4F5F8" : "#FFF",
                          color: "#000",
                          lineHeight: "21px",
                        },
                        "& .MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-colorPrimary":
                          {
                            borderRadius: isUsVersion
                              ? "8px !important"
                              : "6px !important",
                          },
                      }}
                      InputProps={{
                        style: {
                          borderRadius: isUsVersion ? "8px" : "6px",
                          background: isUsVersion ? "#F4F5F8" : undefined,
                          border: isUsVersion ? "1px solid #FAFBFC" : undefined,
                        },
                      }}
                      placeholder="Type here..."
                      name="info"
                      onBlur={handleBlur}
                      multiline
                      rows={
                        additionalInformationInitialValue.length > 0 ? 7 : 3
                      }
                      value={values?.info}
                      onChange={handleChange}
                      error={errors.info && touched.info}
                      helperText={
                        errors.info && touched.info ? errors.info : ""
                      }
                    />
                  </Grid>

                  {/* Terms and Conditions */}
                  <Grid item xs={12}>
                    <div>
                      <TermsConditionBtn
                        checkedPolicy={() => {
                          setFieldValue("termsAccepted", !values.termsAccepted);
                          checkedPolicy();
                        }}
                        isChecked={values.termsAccepted}
                      />
                      {errors.termsAccepted && touched.termsAccepted && (
                        <p className="text-[#d32f2f] text-[12px] mt-1">
                          {errors.termsAccepted}
                        </p>
                      )}
                    </div>
                  </Grid>

                  {/* ReCAPTCHA */}
                  <Grid item xs={12}>
                    {ReCAPTCHA && (
                      <ReCAPTCHA
                        style={{
                          transform: " scale(0.8)",
                          transformOrigin: "0 0",
                        }}
                        className="recaptcha"
                        name="recaptcha"
                        isChecked={isChecked}
                        value={values?.recaptcha}
                        sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
                        onChange={(value) => {
                          setFieldValue("recaptcha", value);
                        }}
                        size="normal"
                      />
                    )}
                    {errors.recaptcha && touched.recaptcha ? (
                      <p
                        style={{
                          fontSize: "12px",
                        }}
                        className="text-[#d32f2f]"
                      >
                        {errors.recaptcha}
                      </p>
                    ) : (
                      ""
                    )}
                  </Grid>

                  {/* Submit Button */}
                  <Grid item xs={12}>
                    <button
                      type="submit"
                      className={`w-full text-[#FFFFFF] cursor-pointer h-[48px] rounded-[8px] bg-[#EE4137] font-semibold text-[16px] disabled:bg-[grey]`}
                      disabled={isLoading}
                    >
                      {isLoading
                        ? "Submitting"
                        : submitButtonLabel.length > 0
                        ? submitButtonLabel
                        : "Submit"}
                    </button>
                  </Grid>
                  {/* Cancel Button for US Version */}
                  {isUsVersion && (
                    <Grid item xs={12}>
                      <button
                        type="button"
                        onClick={handleClose}
                        className="w-full mt-2 text-black bg-white border border-black rounded-[8px] h-[48px] font-semibold text-[16px]"
                      >
                        Cancel
                      </button>
                    </Grid>
                  )}
                </Grid>
              );
            }}
          </Form>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <div className="relative w-30 h-30 mb-6">
            <Image
              src={getCDNImageUrl("solidGreenContactUs.svg")}
              alt="Success"
              width={120}
              height={120}
              className="object-contain"
            />
          </div>
          <p className="text-[#4D4D4D] leading-[120%] font-mukta sm:text-[24px] font-medium mb-6">
            Your request has been sent successfully! We will get back to you
            shortly.
          </p>
          {isUsVersion && (
            <button
              type="button"
              onClick={() => {
                setContactUs((prev) => ({ ...prev, isSuccess: false }));
                if (handleClose) {
                  handleClose();
                }
              }}
              className="w-full mt-2 text-white bg-[#EE4137] rounded-[8px] h-[48px] font-semibold text-[16px]"
            >
              Close
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ContactElchemy;

const schema = Yup.object().shape({
  Name: Yup.string().trim().required("Name is required"),
  // FirstName: Yup.string().trim().required("First Name is required"),
  // LastName: Yup.string().trim().required("Last Name is required"),
  EmailId: Yup.string()
    .matches(
      /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
      "Enter a valid email address"
    )
    .required("Email is required"),
  Country: Yup.string().trim(),
  MobilewithCountryCode: Yup.string().typeError(
    "That doesn't look like a phone number"
  ),
  Services: Yup.string().trim(),
  recaptcha: Yup.string().required("Please enter a recapatcha"),
  info: Yup.string().max(500, "Information exceeds 500 characters"),
  termsAccepted: Yup.boolean()
    .oneOf([true], "Please accept the terms and conditions")
    .required("Please accept the terms and conditions"),
});
